import { create } from 'zustand';

export const createVideoFragmentStore = (initialState = {}) =>
  create((set, get) => ({
    injectDependentStores: (stores) => {
      set({ stores });
    },

    storeState: (name) => {
      if (!get().stores[name])
        throw new Error(`${name} is not a valid dependency in this store.`);
      return get().stores[name].getState();
    },

    createFragment: (fragmentId, SessionTrack, videoRef, initialState) => {
      const state = {
        isPlaying: initialState.isPlaying || false, // if the video is playing.
        currentTime: 0, // Current time of the player, this will continue to be updated as the video is playing
        duration: 0, // Duration of the annotation that will play.
        startTime: 0, // The start time of the annotation
        endTime: 0, // The end time of the annotation
        leadInTime: 0, // The lead in time of the fragment
        leadOutTime: 0, // The lead out time of the fragment
        progressPercentage: 0, // Progress percentage of the fragment that will play.
      };

      const newState = {
        ...state,
        ...initialState,
      };
      // now run some initial setups...
      newState.duration = newState.endTime - newState.startTime;

      set({
        fragments: {
          ...(get()[fragmentId] = { SessionTrack, videoRef, state: newState }),
        },
      });
    },
    deleteFragment: (fragmentId) => {
      // Remove event listeners
      get().clearTimer(fragmentId);
      // remove the fragment id from the store
      set({
        fragments: {
          ...(get()[fragmentId] = undefined),
        },
      });
    },
    getFragment: (fragmentId) => get()[fragmentId],

    play: (fragmentId, fromTime = null) => {
      // Get the current fragment state
      const fragment = { ...get()[fragmentId] };

      // if the from time is null, then use the currentTime value
      if (!fromTime) {
        fromTime = fragment.state.currentTime;
      }

      if (fragment) {
        const videoRef = fragment.videoRef?.current;

        // Create a new state object to ensure proper updates
        const updatedState = { ...fragment.state };
        updatedState.isPlaying = true;

        // Set the current time to the start time of the fragment or fromTime if provided
        updatedState.currentTime =
          fromTime !== null ? fromTime : updatedState.startTime;

        // Update the fragment with the new state
        const updatedFragment = { ...fragment, state: updatedState };

        // Check if the video exists and is not already playing
        if (videoRef) {
          // If fromTime is provided, set the video's current time
          if (fromTime !== null) {
            videoRef.currentTime = fromTime;
          } else if (updatedState.startTime) {
            videoRef.currentTime = updatedState.startTime;
          }

          // Start playing the video if it's not already playing
          if (!videoRef.playing) {
            videoRef
              .play()
              .catch((err) => console.error('Error playing video:', err));
          }
        }

        // Update the store with the new fragment state
        set({ [fragmentId]: updatedFragment });
      }
    },

    updateTime: (fragmentId, time) => {
      const fragment = { ...get()[fragmentId] };

      if (fragment) {
        // Create a new state object to ensure proper updates
        const updatedState = { ...fragment.state };

        // Ensure time is a number
        const actualVideoTime = typeof time === 'number' ? Math.floor(time) : 0;

        // Only update if the time has changed by at least 1 second
        if (Math.abs(actualVideoTime - updatedState.currentTime) >= 1) {
          updatedState.currentTime = actualVideoTime;

          // Calculate progress percentage
          updatedState.progressPercentage =
            (updatedState.currentTime - updatedState.startTime) /
            (updatedState.endTime - updatedState.startTime);

          // When the percentage is 100 or we've reached the end time, stop the video
          if (
            updatedState.progressPercentage >= 1 ||
            updatedState.currentTime >= updatedState.endTime
          ) {
            updatedState.isPlaying = false;

            // Pause the video if it exists
            if (fragment.videoRef?.current) {
              fragment.videoRef.current.pause();
            }
          }

          // Update the fragment with the new state
          const updatedFragment = { ...fragment, state: updatedState };

          // Update the store with the new fragment state
          set({ [fragmentId]: updatedFragment });
        }
      }
    },
    clearTimer: (fragmentId) => {
      // This function is kept for backward compatibility
      // but is no longer needed with the direct update approach
    },
    pause: (fragmentId) => {
      const fragment = { ...get()[fragmentId] };

      if (fragment) {
        const videoRef = fragment.videoRef?.current;

        // Create a new state object to ensure proper updates
        const updatedState = { ...fragment.state };
        updatedState.isPlaying = false;

        // Update the fragment with the new state
        const updatedFragment = { ...fragment, state: updatedState };

        // Check if the video exists and is currently playing
        if (videoRef && fragment.state.isPlaying) {
          videoRef.pause();
        }

        // Update the store with the new fragment state
        set({ [fragmentId]: updatedFragment });
      }
    },
    stepBackward: (fragmentId) => {
      const fragment = { ...get()[fragmentId] };
      if (fragment) {
        fragment.state.currentTime = Math.max(
          fragment.state.startTime,
          fragment.state.currentTime - 1
        );
        // actually step the video if it exists
        if (fragment.videoRef?.current) {
          fragment.videoRef.current.currentTime = fragment.state.currentTime;
        }
        set({ [fragmentId]: fragment });
      }
    },
    stepForward: (fragmentId) => {
      const fragment = { ...get()[fragmentId] };
      if (fragment) {
        fragment.state.currentTime = Math.min(
          fragment.state.endTime,
          fragment.state.currentTime + 1
        );
        // actually step the video if it exists
        if (fragment.videoRef?.current) {
          fragment.videoRef.current.currentTime = fragment.state.currentTime;
        }
        set({ [fragmentId]: fragment });
      }
    },
    adjustStartTime: (fragmentId, startTime) => {
      const fragment = { ...get()[fragmentId] };
      if (fragment) {
        // ensures we are not seeking outside the fragment bounds
        fragment.state.startTime = startTime;
        fragment.state.duration =
          fragment.state.endTime - fragment.state.startTime;
        set({ [fragmentId]: fragment });
      }
    },
    adjustEndTime: (fragmentId, endTime) => {
      const fragment = { ...get()[fragmentId] };
      if (fragment) {
        // ensures we are not seeking outside the fragment bounds
        fragment.state.endTime = endTime;
        fragment.state.duration =
          fragment.state.endTime - fragment.state.startTime;
        set({ [fragmentId]: fragment });
      }
    },
  }));
